import { Component } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import Enumerable from 'linq';
import { EmployeeService } from '../../core/services/employee.service';
import { ToastService } from '../../core/services/toast-service';
import { EmployeeCacheService } from '../../core/services/employee-cache.service';
import { Employee, EmployeePermission, PermissionType } from '../../shared/models/employee/employees.model';
import { AtlasAppId, EmployeePermissionType } from '../../shared/models/employee/employee-permissions-type.model';
import { ToastMode } from '../../shared/models/toast/toast-mode.enum';

@Component({
  selector: 'app-permissions',
  templateUrl: './permissions.component.html'
})
export class PermissionsComponent {
  public isLoading: boolean = true;
  public isLoadingEmployee: boolean = false;
  public isSaving: boolean = false;

  public employees: Employee[] = [];
  public filteredEmployees: Employee[] = [];
  public permissionTypes: PermissionType[] = [];
  public searchText: string = '';

  public selected: Employee | null = null;
  public checked: { [id: string]: boolean } = {};

  private currentUid: string;
  private employeeRequest: Subscription | null = null;

  constructor(
    private router: Router,
    private location: Location,
    private employeeService: EmployeeService,
    private toastService: ToastService,
    private employeeCacheService: EmployeeCacheService
  ) {
    this.currentUid = this.employeeCacheService.uid;
    this.loadData();
  }

  public employeeName = (employee: Employee) => (employee.preferredName || employee.firstName)
    ? `${employee.preferredName || employee.firstName} ${employee.lastName ?? ''}`.trim()
    : employee.username ?? 'No name';

  // Super Admin covers everything, so the other boxes show checked and locked while it's on
  public isLockedBySuperAdmin = (permissionId: string) => permissionId !== EmployeePermissionType.SuperAdmin && !!this.checked[EmployeePermissionType.SuperAdmin];

  // Opened directly or right after login, so there's no page in the app to go back to
  public onBackClick = () => {
    if (this.router.lastSuccessfulNavigation?.previousNavigation) {
      this.location.back();
    } else {
      this.router.navigate(['/']);
    }
  }

  public onSearchChange = () => {
    const search = this.searchText.trim().toLowerCase();
    this.filteredEmployees = Enumerable.from(this.employees)
      .where(x => !search
        || this.employeeName(x).toLowerCase().includes(search)
        || (x.employeeNumber ?? '').toLowerCase().includes(search)
        || (x.username ?? '').toLowerCase().includes(search))
      .toArray();
  }

  public onEmployeeClick = (employee: Employee) => {
    this.employeeRequest?.unsubscribe();
    this.selected = employee;
    this.checked = {};
    this.isLoadingEmployee = true;
    this.employeeRequest = this.employeeService.getEmployeePermissions(employee.uid).subscribe({
      next: (result) => {
        this.setChecked(result[0]?.permissions ?? []);
      },
      error: (err) => {
        this.toastService.show('Error loading permissions.', ToastMode.Danger);
        console.log(err);
      }
    });
    this.employeeRequest.add(() => {
      this.isLoadingEmployee = false;
    });
  }

  public onSaveClick = () => {
    const uid = this.selected!.uid;
    const permissions = Object.keys(this.checked).filter(id => this.checked[id]);
    this.isSaving = true;
    this.employeeService.saveEmployeePermissions(uid, permissions).subscribe({
      next: (employee) => {
        if (this.selected?.uid === uid) {
          this.setChecked(employee.permissions ?? []);
        }
        this.toastService.show('Permissions saved.', ToastMode.Success);
      },
      error: (err) => {
        this.toastService.show(err?.error?.error?.message ?? 'Error saving permissions.', ToastMode.Danger);
        console.log(err);
      }
    }).add(() => {
      this.isSaving = false;
    });
  }

  private setChecked = (permissions: EmployeePermission[]) => {
    this.checked = {};
    permissions
      .filter(x => x.appId === AtlasAppId)
      .forEach(x => this.checked[x.id] = true);
  }

  private loadData = () => forkJoin([
    this.employeeService.getEmployees(),
    this.employeeService.getPermissionTypes()
  ]).subscribe({
    next: ([
      employees,
      permissionTypes
    ]) => {
      // The same person can come back twice (UKG and AD), the UKG record comes first
      this.employees = Enumerable.from(employees)
        .where(x => x.uid !== this.currentUid)
        .distinct(x => x.uid)
        .orderBy(x => this.employeeName(x).toLowerCase())
        .toArray();
      this.permissionTypes = permissionTypes;
      this.onSearchChange();
    },
    error: (err) => {
      this.toastService.show('Error loading data.', ToastMode.Danger);
      console.log(err);
    }
  }).add(() => {
    this.isLoading = false;
  });
}
